import { useEffect } from "react";
import { useLang } from "./i18n";

const SITE_URL = (process.env.REACT_APP_SITE_URL || window.location.origin).replace(/\/$/, "");
const DEFAULT_IMAGE = `${SITE_URL}/brand/cream/dlc-og-1200x630.png`;

// Fuori dal dominio di produzione (preview, staging) nulla va indicizzato: evita contenuti duplicati.
const OFF_PRODUCTION = (() => {
  try {
    const host = new URL(SITE_URL).hostname;
    return host !== window.location.hostname;
  } catch (e) {
    return false;
  }
})();

const setMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const setAlternates = (path) => {
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
  const variants = [["it", ""], ["en", "/en"], ["es", "/es"], ["x-default", ""]];
  variants.forEach(([code, prefix]) => {
    const el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", code);
    el.setAttribute("href", `${SITE_URL}${prefix}${path}`);
    document.head.appendChild(el);
  });
};

// Preload dell'immagine hero: anticipa il download dell'elemento LCP
const setPreloadImage = (href, srcset) => {
  let el = document.head.querySelector('link[rel="preload"][as="image"]');
  if (!href) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "preload");
    el.setAttribute("as", "image");
    el.setAttribute("fetchpriority", "high");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  if (srcset) {
    el.setAttribute("imagesrcset", srcset);
    el.setAttribute("imagesizes", "100vw");
  }
};

// Title, description, canonical, hreflang e card social della pagina corrente
export function useSeo({ title, description, image, path, type = "website", noindex = false, preloadImage, preloadSrcSet }) {
  const { lang } = useLang();
  useEffect(() => {
    if (!title) return;
    const cleanPath = path || window.location.pathname.replace(/^\/(en|es)(?=\/|$)/, "") || "/";
    const prefix = lang === "it" ? "" : `/${lang}`;
    const url = SITE_URL + prefix + cleanPath;
    const img = image ? (image.startsWith("http") ? image : SITE_URL + image) : DEFAULT_IMAGE;
    const desc = description || "";

    const blocked = noindex || OFF_PRODUCTION;
    document.title = title;
    setMeta("name", "description", desc);
    setMeta("name", "robots", blocked ? "noindex, follow" : "index, follow");
    setLink("canonical", url);
    if (blocked) {
      document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    } else {
      setAlternates(cleanPath);
    }

    setMeta("property", "og:site_name", "De Los Cielos");
    setMeta("property", "og:type", type);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", img);
    setMeta("property", "og:locale", lang === "en" ? "en_GB" : lang === "es" ? "es_ES" : "it_IT");

    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", desc);
    setMeta("name", "twitter:image", img);
    setPreloadImage(preloadImage, preloadSrcSet);
  }, [title, description, image, path, type, noindex, lang, preloadImage, preloadSrcSet]);
}

export { SITE_URL };
